import type { TFunction } from 'i18next';
import { useGoals } from '../../../api/strategy-client/strategy-client.hooks';
import type { Goal } from '../../../api/strategy-client/strategy-client.type';
import type { StrategyLevelConfig } from '../strategy-config.type';
import { GoalsFilters } from './goals-filters';
import { GoalsMainSection } from './goals-main-section';
import { GoalsOverview } from './goals-overview';
import { GoalsSideSection } from './goals-side-section';

/**
 * Build the Goals strategy level configuration against the caller's
 * translation function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {StrategyLevelConfig<Goal>} Strategy-level config bound to `t`
 */
export const getGoalsConfig = (t: TFunction): StrategyLevelConfig<Goal> => ({
  level: 'goals',
  tabId: 'goals',

  labels: {
    pageTitle: `${t('common.strategic_goals')} - Bloomfilter`,
    overviewTitle: 'KPIs Overview',
    filtersTitle: 'Filters',
    emptyLabel: `No ${t('common.strategic_goals').toLowerCase()} found`,
    byStatusTitle: `${t('common.strategic_goals')} by Status`,
    byStateTitle: `${t('common.strategic_goals')} by State`,
    searchPlaceholder: `Search ${t('common.strategic_goals').toLowerCase()}...`,
  },

  features: {
    hasTreeMapView: false,
    hasCreatePresentation: true,
    hasExport: true,
    hasSearch: true,
    hasAddNew: true,
    hasFilters: true,
    hasSidePanel: true,
  },

  hooks: {
    useItems: () => {
      const { goals, query } = useGoals();
      return { items: goals, query };
    },
  },

  components: {
    Filters: GoalsFilters,
    Overview: GoalsOverview,
    MainSection: GoalsMainSection,
    SideSection: GoalsSideSection,
  },
});
